import {
  useState,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  PropsWithChildren,
} from 'react';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import createPalette from '@mui/material/styles/createPalette';
import baseTheme, {
  getPaletteOptionsFromColorScheme,
  setMuiCSSVariables,
  setShadcnThemeCSSVariables,
} from '../../styles/theme';
import { useAppSettings } from '../AppSettingsProvider';

interface ThemeContextValue {
  theme: Theme;
  updateTheme: (newThemeOptions: Partial<typeof baseTheme>) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeManager({ children }: PropsWithChildren) {
  const {
    appSettings: { colorScheme },
  } = useAppSettings();
  const [themeOptions, setThemeOptions] = useState(baseTheme);

  const updateTheme = useCallback(
    (newThemeOptions: Partial<typeof baseTheme>) =>
      setThemeOptions((prevOptions) => ({
        ...prevOptions,
        ...newThemeOptions,
      })),
    [setThemeOptions]
  );

  const contextValue = useMemo(() => {
    const muiTheme = createTheme({
      ...themeOptions,
      palette: createPalette(getPaletteOptionsFromColorScheme(colorScheme)),
    });

    return { theme: muiTheme, updateTheme };
  }, [themeOptions, updateTheme, colorScheme]);

  const { theme } = contextValue;

  useEffect(() => {
    setShadcnThemeCSSVariables({ appSettingsColorScheme: colorScheme });
    setMuiCSSVariables(theme.palette);
  }, [theme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <GlobalStyles
        styles={{
          a: {
            textDecoration: 'none',
            color: theme.palette.primary.main,
          },
        }}
      />
      <CssBaseline />
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeManager(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeManager must be used within a ThemeManager');
  }
  return context;
}
